import { baseClasses } from '@weatherbug/common';
import { SitemapProvider } from '../providers/sitemapProvider';
import { CountriesByContinentResult, CitiesByCountryOrStateResult, StatesByCountryType } from '../../components/sitemap/common/types';

interface SelectContinentPayload {
    continentName: string;
    continentRoute: string;
}

interface SelectCountryPayload extends Partial<SelectContinentPayload> {
    countryName: string;
    countryRoute: string;
    countryIso3: string;
}

interface SelectStatePayload extends Partial<SelectCountryPayload> {
    stateName: string;
    stateCode?: string;
    stateRoute?: string;
}

const { AsyncAction } = baseClasses;
export const GET_SITEMAP_COUNTRIES_BY_CONTINENT = new AsyncAction('GET_SITEMAP_COUNTRIES_BY_CONTINENT');
export const GET_SITEMAP_STATES_BY_COUNTRY = new AsyncAction('GET_SITEMAP_STATES_BY_COUNTRY');
export const GET_SITEMAP_CITIES_BY_COUNTRY = new AsyncAction('GET_SITEMAP_CITIES_BY_COUNTRY');
export const GET_TOP_100_SITEMAP_CITIES_BY_STATE_CODE = new AsyncAction('GET_TOP_100_SITEMAP_CITIES_BY_STATE_CODE');
export const SELECT_SITEMAP_CONTINENT = 'SELECT_SITEMAP_CONTINENT';
export const SELECT_SITEMAP_COUNTRY = 'SELECT_SITEMAP_COUNTRY';
export const SELECT_SITEMAP_STATE = 'SELECT_SITEMAP_STATE';

export type SelectContinentActionType = {
    type: typeof SELECT_SITEMAP_CONTINENT;
    payload: SelectContinentPayload;
}

export type SelectCountryActionType = {
    type: typeof SELECT_SITEMAP_COUNTRY;
    payload: SelectCountryPayload;
}

export type SelectStateActionType = {
    type: typeof SELECT_SITEMAP_STATE;
    payload: SelectStatePayload;
}

export type GetSitemapCountriesByContinentType = {
    type: typeof GET_SITEMAP_COUNTRIES_BY_CONTINENT;
    payload: CountriesByContinentResult | any;
}

export type GetSitemapStatesByCountryType = {
    type: typeof GET_SITEMAP_STATES_BY_COUNTRY;
    payload: StatesByCountryType | any;
}

export type GetSitemapCitiesByCountryType = {
    type: typeof GET_SITEMAP_CITIES_BY_COUNTRY;
    payload: CitiesByCountryOrStateResult | any;
}

export type GetSitemapCitiesByStateCodeType = {
    type: typeof GET_TOP_100_SITEMAP_CITIES_BY_STATE_CODE;
    payload: CitiesByCountryOrStateResult | any;
}

export function getCountriesByContinent(continent: string): GetSitemapCountriesByContinentType {
    return {
        type: GET_SITEMAP_COUNTRIES_BY_CONTINENT.base,
        payload: getCountriesByContinentName(continent)
    };
}

async function getCountriesByContinentName(continentName: string): Promise<CountriesByContinentResult | any> {
    const op = new SitemapProvider();
    return await op.getCountriesByContinent(continentName);
}

export function getStatesByCountry(): GetSitemapStatesByCountryType {
    return {
        type: GET_SITEMAP_STATES_BY_COUNTRY.base,
        payload: getStates()
    };
}

async function getStates(): Promise<StatesByCountryType | any> {
    const op = new SitemapProvider();
    return await op.getStatesByCountry();
}

export function getCitiesByCountry(countryIso3: string): GetSitemapStatesByCountryType {
    return {
        type: GET_SITEMAP_CITIES_BY_COUNTRY.base,
        payload: getCitiesByCountryAsync(countryIso3)
    };
}

async function getCitiesByCountryAsync(countryIso3: string): Promise<CitiesByCountryOrStateResult | any> {
    const op = new SitemapProvider();
    return await op.getCitiesByCountry(countryIso3);
}

export function getTop200CitiesByState(stateCode: string): GetSitemapCitiesByStateCodeType {
    return {
        type: GET_TOP_100_SITEMAP_CITIES_BY_STATE_CODE.base,
        payload: getTop200CitiesByStateAsync(stateCode)
    };
}

async function getTop200CitiesByStateAsync(stateCode: string): Promise<CitiesByCountryOrStateResult | any> {
    const op = new SitemapProvider();
    return await op.getTop200CitiesByState(stateCode);
}

export function selectContinentAction(continentName: string, continentRoute: string): SelectContinentActionType {
    return {
        type: SELECT_SITEMAP_CONTINENT,
        payload: {
            continentName,
            continentRoute
        }
    };
}

export function selectCountryAction(
    countryName: string, 
    countryIso3: string, 
    countryRoute: string, 
    continentName?: string, 
    continentRoute?: string): SelectCountryActionType {
    return {
        type: SELECT_SITEMAP_COUNTRY,
        payload: {
            countryName,
            countryIso3,
            countryRoute,
            continentName,
            continentRoute
        }
    };
}

export function selectStateAction(
    countryName: string, 
    countryIso3: string, 
    stateName: string, 
    stateRoute: string, 
    stateCode?: string,
    countryRoute?: string,
    continentName?: string, 
    continentRoute?: string): SelectStateActionType {
    return {
        type: SELECT_SITEMAP_STATE,
        payload: {
            countryName,
            countryIso3,
            stateName,
            stateRoute,
            stateCode,
            countryRoute,
            continentName,
            continentRoute
        }
    };
}